import { applySnapshot, flow, Instance, types, cast } from 'mobx-state-tree';
import axios from 'axios';
import ObjectPath from 'object-path';

import endpoints from '@configs/endpoints';
import { baseUrl } from '@configs/variables';
import Common from '@store/common';
import { IBelvoPaymentData } from '@containers/checkout/payment-form/types';
import {
  BELVO_SUPPORTED_BANKS_BY_CODE,
  BELVO_SUPPORTED_BANKS_CODES,
  Partner,
  PaymentLinkStatus,
} from '@constants/paymentLink';

const BankAccount = types.compose(
  Common,
  types.model({
    bankAccountNumber: types.optional(types.string, ''),
    documentNumber: types.optional(types.string, ''),
    bankName: types.optional(types.string, ''),
  }),
);

const BankAccountErrors = types.model({
  documentNumber: types.optional(types.boolean, false),
  bankAccountNumber: types.optional(types.boolean, false),
  message: types.optional(types.string, ''),
});

const BelvoStore = types
  .compose(
    Common,
    types.model({
      bankAccount: types.optional(BankAccount, {}),
      flags: types.optional(
        types.model({
          requestingDirectDebitSubscription: types.optional(types.boolean, false),
        }),
        {},
      ),
      errors: types.optional(
        types.model({
          bankAccount: types.optional(BankAccountErrors, {}),
          process: types.optional(
            types.model({
              directDebitSubscription: types.optional(types.boolean, false),
            }),
            {},
          ),
        }),
        {},
      ),
      success: types.optional(
        types.model({
          directDebitSubscription: types.optional(types.boolean, false),
        }),
        {},
      ),
    }),
  )
  .views((self) => ({
    get bankAccountNumber() {
      return self.bankAccount.bankAccountNumber.replace(/\s/g, '');
    },
    get bankName() {
      return self.bankAccount.bankName.replace(/\s/g, '');
    },
    get getDocumentNumber() {
      return self.bankAccount.documentNumber.replace(/\s/g, '');
    },
    get hasBankAccountErrors() {
      return Object.values(self.errors.bankAccount).some((value) => value === true);
    },
    get hasDirectDebitSubscriptionProcessErrors() {
      return self.errors.process.directDebitSubscription;
    },
    get isProcessing() {
      return self.flags.requestingDirectDebitSubscription;
    },
  }))
  .actions((self) => ({
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    updateField: (field: string, value: any) => {
      ObjectPath.set(self, field, value);
      applySnapshot(self, self);
    },
    resetBankAccountData: () => {
      self.bankAccount.documentNumber = '';
      self.bankAccount.bankAccountNumber = '';
      self.bankAccount.bankName = '';
    },
    resetBankAccountValidations: () => {
      self.errors.bankAccount.documentNumber = false;
      self.errors.bankAccount.bankAccountNumber = false;
      self.errors.bankAccount.message = '';
    },
    resetProcessErrors: () => {
      self.errors.process.directDebitSubscription = false;
    },
    resetFlags: () => {
      self.flags.requestingDirectDebitSubscription = false;
    },
    resetSuccessStatus: () => {
      self.success.directDebitSubscription = false;
    },
  }))
  .actions((self) => ({
    resetState: () => {
      self.resetBankAccountData();
      self.resetBankAccountValidations();
      self.resetFlags();
      self.resetSuccessStatus();
    },
    resetTransactionState: () => {
      self.resetFlags();
      self.resetBankAccountValidations();
      self.resetProcessErrors();
      self.resetSuccessStatus();
    },
  }))
  .actions((self) => ({
    validateBankAccountNumberData: (bankAccountNumber: string) => {
      const code = bankAccountNumber.substring(0, 3) as keyof typeof BELVO_SUPPORTED_BANKS_BY_CODE;
      if (!BELVO_SUPPORTED_BANKS_CODES.includes(code)) {
        self.errors.bankAccount.bankAccountNumber = true;
        self.errors.bankAccount.message = 'Banco no soportado';
        self.resetBankAccountData();
        return;
      }

      self.resetBankAccountValidations();
      self.bankAccount.bankAccountNumber = bankAccountNumber;
      self.bankAccount.bankName = cast(BELVO_SUPPORTED_BANKS_BY_CODE[code]);
    },
  }))
  .actions((self) => ({
    createDirectDebitSubscription: flow(function* (payload: IBelvoPaymentData) {
      if (self.hasBankAccountErrors || self.flags.requestingDirectDebitSubscription) {
        return;
      }

      try {
        self.flags.requestingDirectDebitSubscription = true;

        const body = {
          paymentLinkId: payload.paymentLinkId,
          payeeFirstName: payload.payeeFirstName,
          payeeLastName: payload.payeeLastName,
          payeeEmail: payload.payeeEmail.trim(),
          payeePhone: payload.payeePhone.replace(/\s/g, ''),
          partner: Partner.BELVO,
          bankInfo: {
            clabe: payload.bankAccountNumber,
          },
          documentInfo: {
            type: 'RFC',
            number: payload.documentNumber,
          },
        };
        yield axios.post(endpoints.processDirectDebitSubscription, body);
        self.flags.requestingDirectDebitSubscription = false;

        self.resetBankAccountData();
        self.success.directDebitSubscription = true;
        self.globalStore.links.paymentLink.updatePaymentLinkStatus(PaymentLinkStatus.SUBSCRIBED);
        self.globalStore.checkout.setDirectDebitPaymentSuccess();
      } catch (e) {
        self.flags.requestingDirectDebitSubscription = false;
        self.errors.process.directDebitSubscription = true;
        self.updateField('errors.bankAccount.message', `Algo salio mal intenta nuevamente`);
      }
      return { success: false };
    }),
  }));

export type IBelvoStore = Instance<typeof BelvoStore>;
export default BelvoStore;
